import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Pagination } from "./ui/pagination";
import { Layers, Plus, Search, Edit, Trash2, Eye, Package, Calendar, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { rawItemApiService, RawItem } from "../services";
import { PermissionGate } from "./ui/PermissionGate";

export function RawItemListingPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [rawItems, setRawItems] = useState<RawItem[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Client-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20; // Increased to 20 items per page

  // Raw Item management route (from backend permissions)
  const RAW_ITEM_ROUTE = '/raw-items';

  // Load raw items from API
  useEffect(() => {
    loadRawItems();
  }, []);

  const loadRawItems = async () => {
    try {
      setLoading(true);
      const rawItemsData = await rawItemApiService.getRawItems();
      setRawItems(rawItemsData);
    } catch (error) {
      // console.error('Error loading raw items:', error);
      toast.error('Failed to load raw items');
    } finally {
      setLoading(false);
    }
  };

  // Filter raw items based on search term
  const filteredRawItems = rawItems.filter(item => 
    item.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.itemCategory?.categoryName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate total pages based on filtered items
  const totalPages = Math.ceil(filteredRawItems.length / pageSize);
  const totalCount = filteredRawItems.length;

  // Pagination logic - slice the filtered items
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedRawItems = filteredRawItems.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of table when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewRawItem = () => {
    navigate('/raw-items/new');
  };

  const handleEditRawItem = (item: RawItem) => {
    navigate(`/raw-items/${item.id}`);
  };

  const handleViewRawItem = (item: RawItem) => {
    navigate(`/raw-items/${item.id}?view=true`);
  };

  const handleDeleteRawItem = async (item: RawItem) => {
    if (window.confirm(`Are you sure you want to delete ${item.itemName}?`)) {
      try {
        await rawItemApiService.deleteRawItem(item.id.toString());
        await loadRawItems(); // Reload the list
        toast.success('Raw item deleted successfully');
      } catch (error) {
        // console.error('Error deleting raw item:', error);
        toast.error('Failed to delete raw item');
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                <Layers className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-xl">Raw Item Management</CardTitle>
                <p className="text-primary-100 text-sm">Manage your raw materials and inventory</p>
              </div>
            </div>
            <PermissionGate route={RAW_ITEM_ROUTE} action="create">
              <Button
                onClick={handleNewRawItem}
                className="bg-white text-primary-600 hover:bg-primary-50 shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Raw Item
              </Button>
            </PermissionGate>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search and Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search raw items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="text-sm text-gray-600">
              Showing {paginatedRawItems.length > 0 ? startIndex + 1 : 0} - {Math.min(endIndex, totalCount)} of {totalCount} items
              {searchTerm && ` (filtered from ${rawItems.length} total)`}
            </div>
          </div>

          {/* Raw Items Table */}
          <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <Table className="table-fixed w-full">
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <TableHead className="w-16">S No.</TableHead>
                  <TableHead className="w-[300px]">Item Details</TableHead>
                  <TableHead className="w-32">Category</TableHead>
                  <TableHead className="w-40">Specifications</TableHead>
                  <TableHead className="w-32">Unit & Weight</TableHead>
                  <TableHead className="w-32">Buffer Stock</TableHead>
                  <TableHead className="w-28">HSN Code</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="text-center w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading raw items...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : paginatedRawItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                      No raw items found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRawItems.map((item, index) => (
                    <TableRow key={item.id} className="hover:bg-primary-50/50">
                      <TableCell className="font-medium w-16">{(currentPage - 1) * pageSize + index + 1}</TableCell>
                      <TableCell className="w-[300px]">
                        <div className="break-words pr-2 overflow-hidden">
                          <div className="font-medium break-words text-sm leading-tight whitespace-normal">{item.itemName || 'N/A'}</div>
                          <div className="text-xs text-gray-500 mt-1">SKU: {item.sku || 'N/A'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="w-32">
                        <Badge variant="outline" className="border-primary-200 text-primary-700 text-xs">
                          {item.itemCategory?.categoryName || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell className="w-40">
                        <div className="space-y-1 text-xs">
                          <div>Type: {item.type || 'N/A'}</div>
                          <div>Color: {item.color || 'N/A'}</div>
                          <div>Shape: {item.shape || 'N/A'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="w-32">
                        <div className="space-y-1 text-xs">
                          <div>Unit: {item.itemUnit || 'N/A'}</div>
                          <div className="text-gray-500">Weight: {item.weight || 'N/A'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="w-32">
                        <div className="space-y-1 text-xs">
                          <div>Min Buffer: {item.minBufferStock || 'N/A'}</div>
                          <div className="text-gray-500">Grade: {item.grade || 'N/A'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="w-28">
                        <code className="bg-gray-100 px-2 py-1 rounded text-xs break-all">{item.hsn?.hsnCode || 'N/A'}</code>
                      </TableCell>
                      <TableCell className="w-24">
                        <Badge 
                          variant={item.isActive ? "default" : "secondary"}
                          className={item.isActive ? "bg-green-100 text-green-800 text-xs" : "bg-gray-100 text-gray-800 text-xs"}
                        >
                          {item.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="w-32">
                        <div className="flex items-center gap-2">
                          <PermissionGate route={RAW_ITEM_ROUTE} action="view">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewRawItem(item)}
                              className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={RAW_ITEM_ROUTE} action="edit">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditRawItem(item)}
                              className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={RAW_ITEM_ROUTE} action="delete">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteRawItem(item)}
                              className="border-red-200 hover:bg-red-50 hover:border-red-300 text-red-600"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          </div>

          {filteredRawItems.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Layers className="h-12 w-12 mx-auto mb-3 text-primary-300" />
              <p>No raw items found matching your search criteria.</p>
            </div>
          )}

          {/* Pagination */}
          {filteredRawItems.length > 0 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Items</p>
                <p className="text-2xl font-semibold text-primary-700">{rawItems.length}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                <Layers className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Stock</p>
                <p className="text-2xl font-semibold text-green-700">
                  {rawItems.filter((i: RawItem) => i.isActive).length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                <Package className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Low Stock</p>
                <p className="text-2xl font-semibold text-yellow-700">
                  {rawItems.filter((i: RawItem) => !i.isActive).length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center">
                <Truck className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Out of Stock</p>
                <p className="text-2xl font-semibold text-red-700">
                  {rawItems.filter((i: RawItem) => i.minBufferStock < 10).length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                <Package className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div> */}

    </div>
  );
}